/**
 * HidroVerse Water Purification Information Sheet schema.
 *
 * Lean 6-stage sizing form (Dirk 2026-09-09: "We just want to get
 * an understanding of how to size a system"). Structure matches the
 * Lead Onboarding schema exactly (same {sections, fields} shape, same
 * field types) so `SectionEditor` / `StepIndex` / `PrintableDoc`
 * from the onboarding form drop straight in — same visual bar,
 * same left-nav, same autosave/print pipeline.
 *
 * Field types: text | textarea | number | select | multiselect | radio |
 *              bool | upload
 *
 * Stages (per Dirk's checklist):
 *   1. Water Source          4. Clean Water Supply
 *   2. Storage               5. Sizing (backwards calculator)
 *   3. Purification          6. Site & Submitter
 */

const yesNo = ["Yes", "No"];
const yesNoUnknown = ["Yes", "No", "Unknown"];

export const WATER_SHEET_SECTIONS = [
  {
    key: "site",
    title: "1. Your Details",
    description: "So we know who this is for and how to reach you.",
    fields: [
      { key: "business_name", label: "Business / facility name", type: "text", required: true },
      { key: "contact_name", label: "Contact person", type: "text", required: true },
      { key: "contact_email", label: "Email", type: "text", required: true, placeholder: "you@example.com" },
      { key: "contact_phone", label: "Phone", type: "text" },
      { key: "physical_address", label: "Physical address", type: "textarea" },
      { key: "submitter_role", label: "Your role at the facility", type: "text" },
    ],
  },
  {
    key: "source",
    title: "2. Water Source",
    description: "Where does the raw water come from and how much is available?",
    fields: [
      {
        key: "sources",
        label: "Water source(s)",
        type: "multiselect",
        options: ["Borehole", "River", "Dam", "Rainwater", "Municipal", "Spring", "Other"],
        required: true,
      },
      {
        key: "availability_lph",
        label: "Availability — litres per hour",
        type: "number",
        placeholder: "e.g. 500",
      },
      {
        key: "availability_lpd",
        label: "Availability — litres per day",
        type: "number",
        placeholder: "e.g. 8 000",
      },
      {
        key: "water_quality_known",
        label: "Do you have a water analysis?",
        type: "radio",
        options: yesNoUnknown,
      },
      {
        key: "water_analysis_files",
        label: "Upload water analysis (PDF / photo)",
        type: "upload",
        multiple: true,
        show_if: { water_quality_known: "Yes" },
      },
    ],
  },
  {
    key: "storage",
    title: "3. Storage",
    description: "How is water currently stored on site?",
    fields: [
      {
        key: "raw_storage_l",
        label: "Raw water storage capacity (L)",
        type: "number",
        placeholder: "0 if none",
      },
      {
        key: "clean_storage_l",
        label: "Clean water storage capacity (L)",
        type: "number",
        placeholder: "0 if none",
      },
      {
        key: "storage_types",
        label: "Type of storage",
        type: "multiselect",
        options: ["Concrete reservoir", "Plastic tanks", "Steel tanks", "Fibreglass", "Other"],
      },
      {
        key: "storage_age_years",
        label: "Age of storage (years)",
        type: "number",
      },
      {
        key: "storage_cleaned",
        label: "Has storage been cleaned?",
        type: "radio",
        options: yesNoUnknown,
      },
      {
        key: "storage_cleaned_months_ago",
        label: "If yes, how many months ago?",
        type: "number",
        show_if: { storage_cleaned: "Yes" },
      },
    ],
  },
  {
    key: "purification",
    title: "4. Purification",
    description: "Any treatment already installed?",
    fields: [
      {
        key: "purification_installed",
        label: "Any purification currently installed?",
        type: "radio",
        options: yesNo,
        required: true,
      },
      {
        key: "purification_description",
        label: "Describe the existing system",
        type: "textarea",
        placeholder: "Stages, brands, age, known issues — a short paragraph is fine.",
        show_if: { purification_installed: "Yes" },
      },
    ],
  },
  {
    key: "supply",
    title: "5. Clean Water Supply",
    description: "How does water reach the points of use today?",
    fields: [
      {
        key: "current_supply",
        label: "Current supply method",
        type: "textarea",
        placeholder: "Gravity-fed from an elevated tank / pumped on demand / municipal pressure / etc.",
      },
      {
        key: "needs_supply_pumps",
        label: "Need supply pumps / pressure system?",
        type: "radio",
        options: ["Yes", "No", "Unsure"],
      },
    ],
  },
  {
    key: "sizing",
    title: "6. Sizing & Notes",
    description: "Either give us a target litres/day, or fill in the head-count block and we'll calculate.",
    fields: [
      {
        key: "quality_standard",
        label: "Water quality standard required",
        type: "select",
        options: ["SANS 241 (default)", "WHO", "HACCP", "ISO 22000", "Mine health", "Hospitality-brand", "Other"],
      },
      {
        key: "quality_standard_other",
        label: "Standard details",
        type: "text",
        show_if: { quality_standard: "Other" },
      },
      {
        key: "target_lpd",
        label: "Litres required per day",
        type: "number",
        placeholder: "If you know it. Otherwise leave blank and fill in the head-count block below.",
      },
      {
        key: "target_lpm",
        label: "Litres required per month",
        type: "number",
        placeholder: "Alternative if daily is unknown.",
      },
      {
        key: "facility_type",
        label: "Facility type",
        type: "select",
        options: [
          "Lodge / Guesthouse / Hotel",
          "Restaurant",
          "Farm / Agriculture",
          "Factory / Manufacturing",
          "Bottling plant",
          "Hospital / Clinic",
          "Community / Village",
          "Mine",
          "School / Institution",
          "Office / Retail",
          "Residential",
          "Other",
        ],
      },
      {
        key: "people_permanent",
        label: "Permanent people (residents / on-site staff)",
        type: "number",
      },
      {
        key: "people_partial",
        label: "Partial people (guests / visitors)",
        type: "number",
      },
      {
        key: "day_shift",
        label: "Day shift count",
        type: "number",
      },
      {
        key: "night_shift",
        label: "Night shift count",
        type: "number",
      },
      { key: "notes", label: "Anything else we should know", type: "textarea" },
    ],
  },
];

export const WATER_SHEET_FIELD_COUNT = WATER_SHEET_SECTIONS.reduce(
  (n, s) => n + s.fields.length,
  0,
);

/**
 * Per-capita L/day multipliers used by the backwards-calculator on
 * Stage 5. Rough industry rules-of-thumb — good enough for sizing,
 * fine-tuning happens later.
 */
export const FACILITY_LPD_PER_PERSON = {
  "Lodge / Guesthouse / Hotel": 250,
  "Restaurant": 40,
  "Farm / Agriculture": 100,
  "Factory / Manufacturing": 80,
  "Bottling plant": 300,
  "Hospital / Clinic": 400,
  "Community / Village": 100,
  "Mine": 150,
  "School / Institution": 25,
  "Office / Retail": 30,
  "Residential": 200,
  "Other": 100,
};

/**
 * Estimate demand in L/day from head-count block. Returns 0 if we
 * don't have enough info to guess.
 */
export function estimateLpd(data) {
  const type = data?.facility_type || "";
  const perPerson = FACILITY_LPD_PER_PERSON[type];
  if (!perPerson) return 0;
  const perm = Number(data?.people_permanent) || 0;
  const partial = Number(data?.people_partial) || 0;
  const day = Number(data?.day_shift) || 0;
  const night = Number(data?.night_shift) || 0;
  const heads = perm + partial + day + night;
  if (!heads) return 0;
  return Math.round(heads * perPerson);
}
